/**
 * Guided Tests page — shown only in POC_MODE.
 *
 * Renders the seeded test scenarios as an accordion. Each scenario lists
 * numbered steps with "Mark complete" checkboxes; progress persists via
 * markStepComplete(). A trial status card sits at the top (days remaining,
 * token budget used).
 *
 * STUB — Pkg F implements data loading + interactions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  getPocStatus,
  getPocScenarios,
  getPocProgress,
  markStepComplete,
  PocStatus,
  PocScenario,
} from '../lib/pocApi';
import { Icon } from '../components/ui';
import { useTranslation } from 'react-i18next';

interface PocTestCasesProps {
  apiUrl: string;
}

export default function PocTestCases(_props: PocTestCasesProps): JSX.Element {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PocStatus | null>(null);
  const [scenarios, setScenarios] = useState<PocScenario[]>([]);
  const [progress, setProgress] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setError(null);
        const [statusData, scenarioData, progressData] = await Promise.all([
          getPocStatus(),
          getPocScenarios(),
          getPocProgress(),
        ]);

        if (statusData && typeof statusData === 'object' && 'days_remaining' in statusData) {
          setStatus(statusData as PocStatus);
        }
        setScenarios(scenarioData);

        // Build progress map: scenario_id -> max completed step index
        const progressMap = new Map<string, number>();
        progressData.forEach((p) => {
          const current = progressMap.get(p.scenario_id) || -1;
          progressMap.set(p.scenario_id, Math.max(current, p.step_index));
        });
        setProgress(progressMap);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('pages.pocTestCases.failedToLoadTestCases'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleStepCheck = useCallback(
    async (scenarioId: string, stepIndex: number) => {
      setSaving(true);
      try {
        await markStepComplete(scenarioId, stepIndex);
        // Update local progress
        setProgress((prev) => {
          const next = new Map(prev);
          const current = next.get(scenarioId) || -1;
          next.set(scenarioId, Math.max(current, stepIndex));
          return next;
        });
      } catch (err) {
        console.error('Failed to mark step complete:', err);
      } finally {
        setSaving(false);
      }
    },
    []
  );

  if (loading) {
    return (
      <div className="page-content">
        <div className="page-title">
          <h1><Icon name="checkCircle" size={20} /> {t('pages.pocTestCases.guidedTests')}</h1>
          <p className="subtitle">{t('pages.pocTestCases.evaluateAgenti5CapabilitiesWithPrePackaged')}</p>
        </div>
        <p className="muted-text">{t('pages.pocTestCases.loadingTestScenarios')}</p>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1><Icon name="checkCircle" size={20} /> {t('pages.pocTestCases.guidedTests')}</h1>
          <p className="subtitle">{t('pages.pocTestCases.evaluateAgenti5CapabilitiesWithPrePackaged')}</p>
        </div>
      </div>

      {error && (
        <div className="notice error">
          <Icon name="warning" /> {error}
        </div>
      )}

      {/* Status card */}
      {status && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium text-blue-600 dark:text-blue-400">{t('pages.pocTestCases.company')}</div>
              <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">{status.org_slug}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-blue-600 dark:text-blue-400">{t('pages.pocTestCases.daysRemaining')}</div>
              <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                {t('pages.pocTestCases.daysCount', { count: status.days_remaining })}
              </div>
            </div>
            {status.token_usage_today !== undefined && status.daily_token_budget !== undefined && (
              <div>
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">{t('pages.pocTestCases.tokenBudget')}</div>
                <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                  {status.token_usage_today.toLocaleString()} / {status.daily_token_budget.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scenarios accordion */}
      <div className="space-y-3">
        {scenarios.length === 0 ? (
          <div className="ent-empty">
            <span className="icon"><Icon name="inbox" /></span>
            <h4>{t('pages.pocTestCases.noTestScenariosAvailable')}</h4>
            <p>{t('pages.pocTestCases.testScenariosWillAppearHereOnce')}</p>
          </div>
        ) : (
          scenarios.map((scenario) => {
            const isExpanded = expandedScenario === scenario.scenario_id;
            const completedSteps = progress.get(scenario.scenario_id) ?? -1;
            const totalSteps = scenario.steps.length;
            const completionPct = totalSteps > 0 ? ((completedSteps + 1) / totalSteps) * 100 : 0;

            return (
              <div
                key={scenario.scenario_id}
                className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden"
              >
                {/* Accordion header */}
                <button
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.scenario_id)}
                  className="w-full px-4 py-4 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-between gap-3 transition"
                >
                  <div className="flex items-center gap-3 flex-1 text-left">
                    <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                      {scenario.sort_order}. {scenario.title}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {totalSteps > 0 && (
                      <div className="text-sm text-zinc-600 dark:text-zinc-400 whitespace-nowrap">
                        {completedSteps + 1} / {totalSteps}
                      </div>
                    )}
                    <Icon
                      name={isExpanded ? 'chevronDown' : 'chevronRight'}
                      size={20}
                      className="text-zinc-500 dark:text-zinc-400"
                    />
                  </div>
                </button>

                {/* Progress bar */}
                {totalSteps > 0 && (
                  <div className="h-1 bg-zinc-200 dark:bg-zinc-700">
                    <div
                      className="h-full bg-green-500 transition-all duration-300"
                      style={{ width: `${completionPct}%` }}
                    />
                  </div>
                )}

                {/* Accordion content */}
                {isExpanded && (
                  <div className="px-4 py-4 space-y-4 bg-white dark:bg-zinc-900/50">
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">{scenario.description}</p>

                    <div className="space-y-3">
                      {scenario.steps.map((step, stepIndex) => {
                        const isCompleted = completedSteps >= stepIndex;
                        return (
                          <label
                            key={stepIndex}
                            className="flex items-start gap-3 p-3 rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isCompleted}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleStepCheck(scenario.scenario_id, stepIndex);
                                }
                              }}
                              disabled={saving}
                              className="mt-0.5 flex-shrink-0"
                            />
                            <div className="flex-1">
                              <div className={`text-sm font-medium ${
                                isCompleted
                                  ? 'text-zinc-500 dark:text-zinc-500 line-through'
                                  : 'text-zinc-900 dark:text-zinc-100'
                              }`}>{t('pages.pocTestCases.step', { v0: stepIndex + 1, v1: step })}</div>
                            </div>
                            {isCompleted && (
                              <Icon name="check" size={16} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
